/**
 * PunchItemDrawer Component
 * Create / Edit / View a Punch Item
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  DollarSign,
  MapPin,
  Tag,
  User,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import {
  useCreatePunchItemMutation,
  useUpdatePunchItemMutation,
} from '../../services/punchListApi';
import { useGetVendorsQuery } from '../../services/vendorApi';
import type {
  CreatePunchItemRequest,
  PunchItem,
  PunchItemCategory,
  PunchItemPriority,
  UpdatePunchItemRequest,
} from '../../types/punchItem';
import { CurrencyInput, DatePicker, Dropdown, Input, SearchableDropdown, TextArea } from '../forms';

// ============================================================================
// PROPS
// ============================================================================

export interface PunchItemDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  punchItem?: PunchItem | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

// ============================================================================
// FORM DATA TYPE
// ============================================================================

interface PunchItemFormData {
  title: string;
  description: string;
  category: string;
  priority: string;
  trade: string;
  system: string;
  building: string;
  floor: string;
  room: string;
  area: string;
  specificLocation: string;
  assignedToContractor: string;
  assignedToContractorName: string;
  assignedToUser: string;
  assignedToUserName: string;
  dueDate: string;
  hasCostImpact: boolean;
  estimatedCost: number | null;
  backcharge: boolean;
  backchargeAmount: number | null;
}

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

const validationSchema = Yup.object<PunchItemFormData>({
  title: Yup.string().trim().min(1, 'Title is required').max(500).required('Title is required'),
  description: Yup.string().trim().max(5000).default(''),
  category: Yup.string()
    .oneOf(
      ['incomplete', 'defective', 'damaged', 'missing', 'cosmetic', 'other'],
      'Select a valid category'
    )
    .required('Category is required'),
  priority: Yup.string().oneOf(['low', 'medium', 'high', 'critical']).default('medium'),
  trade: Yup.string().trim().max(200).default(''),
  system: Yup.string().trim().max(200).default(''),
  building: Yup.string().trim().max(200).default(''),
  floor: Yup.string().trim().max(100).default(''),
  room: Yup.string().trim().max(200).default(''),
  area: Yup.string().trim().max(200).default(''),
  specificLocation: Yup.string().trim().max(500).default(''),
  assignedToContractor: Yup.string().default(''),
  assignedToContractorName: Yup.string().trim().max(255).default(''),
  assignedToUser: Yup.string().default(''),
  assignedToUserName: Yup.string().trim().max(255).default(''),
  dueDate: Yup.string().default(''),
  hasCostImpact: Yup.boolean().default(false),
  estimatedCost: Yup.number().min(0).nullable().default(null),
  backcharge: Yup.boolean().default(false),
  backchargeAmount: Yup.number().min(0).nullable().default(null),
});

// ============================================================================
// OPTIONS
// ============================================================================

const CATEGORY_OPTIONS = [
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'defective', label: 'Defective' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'missing', label: 'Missing' },
  { value: 'cosmetic', label: 'Cosmetic' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// ============================================================================
// SECTION TOGGLE COMPONENT
// ============================================================================

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const Section: React.FC<SectionProps> = ({ title, icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <span className="text-brand-600">{icon}</span>
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        )}
      </button>
      {open && <div className="px-4 py-4 space-y-4 bg-white">{children}</div>}
    </div>
  );
};

// ============================================================================
// DEFAULT FORM VALUES
// ============================================================================

const defaultFormValues: PunchItemFormData = {
  title: '',
  description: '',
  category: 'incomplete',
  priority: 'medium',
  trade: '',
  system: '',
  building: '',
  floor: '',
  room: '',
  area: '',
  specificLocation: '',
  assignedToContractor: '',
  assignedToContractorName: '',
  assignedToUser: '',
  assignedToUserName: '',
  dueDate: '',
  hasCostImpact: false,
  estimatedCost: null,
  backcharge: false,
  backchargeAmount: null,
};

// ============================================================================
// DRAWER COMPONENT
// ============================================================================

const PunchItemDrawer: React.FC<PunchItemDrawerProps> = ({
  isOpen,
  onClose,
  punchItem,
  mode,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId() ?? '';
  const toast = useToast();

  const [createPunchItem, { isLoading: isCreating }] = useCreatePunchItemMutation();
  const [updatePunchItem, { isLoading: isUpdating }] = useUpdatePunchItemMutation();
  const { data: vendorsData } = useGetVendorsQuery({ limit: 200 });

  const isReadOnly = mode === 'view';
  const isLoading = isCreating || isUpdating;
  const drawerTitle =
    mode === 'create'
      ? 'Add Punch Item'
      : mode === 'edit'
        ? 'Edit Punch Item'
        : 'Punch Item Details';

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PunchItemFormData>({
    resolver: yupResolver(validationSchema) as never,
    defaultValues: defaultFormValues,
  });

  const hasCostImpact = watch('hasCostImpact');
  const backcharge = watch('backcharge');

  // Populate form when editing
  useEffect(() => {
    if (punchItem && (mode === 'edit' || mode === 'view')) {
      reset({
        title: punchItem.title,
        description: punchItem.description ?? '',
        category: punchItem.category,
        priority: punchItem.priority,
        trade: punchItem.trade ?? '',
        system: punchItem.system ?? '',
        building: punchItem.building ?? '',
        floor: punchItem.floor ?? '',
        room: punchItem.room ?? '',
        area: punchItem.area ?? '',
        specificLocation: punchItem.specificLocation ?? '',
        assignedToContractor: punchItem.assignedToContractor ?? '',
        assignedToContractorName: punchItem.assignedToContractorName ?? '',
        assignedToUser: punchItem.assignedToUser ?? '',
        assignedToUserName: punchItem.assignedToUserName ?? '',
        dueDate: punchItem.dueDate ? new Date(punchItem.dueDate).toISOString().split('T')[0] : '',
        hasCostImpact: punchItem.hasCostImpact,
        estimatedCost: punchItem.estimatedCost ?? null,
        backcharge: punchItem.backcharge,
        backchargeAmount: punchItem.backchargeAmount ?? null,
      });
    } else if (mode === 'create') {
      reset(defaultFormValues);
    }
     
  }, [punchItem, mode, reset]);

  const onSubmit = async (data: PunchItemFormData) => {
    if (!projectId) {
      toast.error('No project selected');
      return;
    }

    try {
      if (mode === 'create') {
        const payload: CreatePunchItemRequest = {
          title: data.title,
          category: data.category as PunchItemCategory,
          priority: data.priority as PunchItemPriority,
          description: data.description || undefined,
          trade: data.trade || undefined,
          system: data.system || undefined,
          building: data.building || undefined,
          floor: data.floor || undefined,
          room: data.room || undefined,
          area: data.area || undefined,
          specificLocation: data.specificLocation || undefined,
          assignedToContractor: data.assignedToContractor || undefined,
          assignedToContractorName: data.assignedToContractorName || undefined,
          dueDate: data.dueDate || undefined,
          hasCostImpact: data.hasCostImpact,
          estimatedCost: data.estimatedCost ?? undefined,
          backcharge: data.backcharge,
          backchargeAmount: data.backchargeAmount ?? undefined,
        };

        const result = await createPunchItem({ projectId, data: payload }).unwrap();
        if (result.success) {
          toast.success('Punch item created successfully');
          onSuccess?.();
          onClose();
        }
      } else if (mode === 'edit' && punchItem) {
        const payload: UpdatePunchItemRequest = {
          title: data.title,
          category: data.category as PunchItemCategory,
          priority: data.priority as PunchItemPriority,
          description: data.description || undefined,
          trade: data.trade || undefined,
          system: data.system || undefined,
          building: data.building || undefined,
          floor: data.floor || undefined,
          room: data.room || undefined,
          area: data.area || undefined,
          specificLocation: data.specificLocation || undefined,
          assignedToContractor: data.assignedToContractor || undefined,
          assignedToContractorName: data.assignedToContractorName || undefined,
          dueDate: data.dueDate || undefined,
          hasCostImpact: data.hasCostImpact,
          estimatedCost: data.estimatedCost ?? undefined,
          backcharge: data.backcharge,
          backchargeAmount: data.backchargeAmount ?? undefined,
        };

        const result = await updatePunchItem({
          projectId,
          id: punchItem._id,
          data: payload,
        }).unwrap();

        if (result.success) {
          toast.success('Punch item updated successfully');
          onSuccess?.();
          onClose();
        }
      }
    } catch (_err) {
      toast.error('An error occurred. Please try again.');
    }
  };

  const vendorOptions = (vendorsData?.data?.vendors ?? []).map(v => ({
    value: v._id,
    label: v.name,
  }));

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />

      {/* Drawer — 90% width via inset-6 for multi-section form */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] left-6 animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <ClipboardCheck className="h-5 w-5 text-brand-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-800">{drawerTitle}</h2>
                {punchItem?.punchNumber && (
                  <p className="text-xs text-gray-500 mt-0.5">{punchItem.punchNumber}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <form
              id="punch-item-form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className="space-y-4"
            >
              {/* Basic Info */}
              <Section
                title="Basic Information"
                icon={<ClipboardCheck className="h-4 w-4" />}
                defaultOpen
              >
                {/* Title */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    {...register('title')}
                    placeholder="Describe the deficiency"
                    disabled={isReadOnly}
                  />
                  {errors.title && (
                    <p className="text-xs text-red-500 mt-1 flex items-center space-x-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{errors.title.message}</span>
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Description</label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        value={field.value}
                        onChange={val => field.onChange(val)}
                        placeholder="Detailed description of the issue"
                        rows={3}
                        disabled={isReadOnly}
                      />
                    )}
                  />
                </div>

                {/* Category / Priority row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="category"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          options={CATEGORY_OPTIONS}
                          value={field.value}
                          onChange={val => field.onChange(val)}
                          placeholder="Select category"
                          disabled={isReadOnly}
                        />
                      )}
                    />
                    {errors.category && (
                      <p className="text-xs text-red-500 mt-1">{errors.category.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Priority</label>
                    <Controller
                      name="priority"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          options={PRIORITY_OPTIONS}
                          value={field.value}
                          onChange={val => field.onChange(val)}
                          placeholder="Select priority"
                          disabled={isReadOnly}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Trade / System row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Trade</label>
                    <Input
                      {...register('trade')}
                      placeholder="e.g. Electrical, Plumbing"
                      disabled={isReadOnly}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">System</label>
                    <Input
                      {...register('system')}
                      placeholder="e.g. HVAC, Fire Suppression"
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                {/* Due Date */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Due Date</label>
                  <Controller
                    name="dueDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        value={field.value}
                        onChange={val => field.onChange(val)}
                        disabled={isReadOnly}
                      />
                    )}
                  />
                </div>
              </Section>

              {/* Location */}
              <Section title="Location" icon={<MapPin className="h-4 w-4" />} defaultOpen>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Building</label>
                    <Input
                      {...register('building')}
                      placeholder="Building name or number"
                      disabled={isReadOnly}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Floor</label>
                    <Input {...register('floor')} placeholder="Floor level" disabled={isReadOnly} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Room</label>
                    <Input
                      {...register('room')}
                      placeholder="Room number or name"
                      disabled={isReadOnly}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Area</label>
                    <Input {...register('area')} placeholder="Area or zone" disabled={isReadOnly} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Specific Location</label>
                  <Input
                    {...register('specificLocation')}
                    placeholder="e.g. North wall, near window"
                    disabled={isReadOnly}
                  />
                </div>
              </Section>

              {/* Assignment */}
              <Section title="Assignment" icon={<User className="h-4 w-4" />} defaultOpen={false}>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Assigned Contractor</label>
                  <Controller
                    name="assignedToContractor"
                    control={control}
                    render={({ field }) => (
                      <SearchableDropdown
                        options={vendorOptions}
                        value={field.value}
                        onChange={val => {
                          field.onChange(val);
                          const vendor = (vendorsData?.data?.vendors ?? []).find(
                            v => v._id === val
                          );
                          if (vendor) {
                            setValue('assignedToContractorName', vendor.name);
                          }
                        }}
                        placeholder="Search contractor..."
                        disabled={isReadOnly}
                      />
                    )}
                  />
                </div>
              </Section>

              {/* Cost Impact */}
              <Section
                title="Cost Impact"
                icon={<DollarSign className="h-4 w-4" />}
                defaultOpen={false}
              >
                {/* Has Cost Impact toggle */}
                <div className="flex items-center space-x-3">
                  <Controller
                    name="hasCostImpact"
                    control={control}
                    render={({ field }) => (
                      <button
                        type="button"
                        onClick={() => !isReadOnly && field.onChange(!field.value)}
                        className={`relative inline-flex h-5 w-10 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                          field.value ? 'bg-brand-600' : 'bg-gray-200'
                        } ${isReadOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                            field.value ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    )}
                  />
                  <span className="text-sm text-gray-700">This item has a cost impact</span>
                </div>

                {hasCostImpact && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Estimated Cost</label>
                    <Controller
                      name="estimatedCost"
                      control={control}
                      render={({ field }) => (
                        <CurrencyInput
                          value={field.value ?? 0}
                          onChange={val => field.onChange(val)}
                          disabled={isReadOnly}
                        />
                      )}
                    />
                  </div>
                )}

                {/* Backcharge toggle */}
                <div className="flex items-center space-x-3 mt-2">
                  <Controller
                    name="backcharge"
                    control={control}
                    render={({ field }) => (
                      <button
                        type="button"
                        onClick={() => !isReadOnly && field.onChange(!field.value)}
                        className={`relative inline-flex h-5 w-10 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                          field.value ? 'bg-brand-600' : 'bg-gray-200'
                        } ${isReadOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                            field.value ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    )}
                  />
                  <span className="text-sm text-gray-700">Backcharge to contractor</span>
                </div>

                {backcharge && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Backcharge Amount</label>
                    <Controller
                      name="backchargeAmount"
                      control={control}
                      render={({ field }) => (
                        <CurrencyInput
                          value={field.value ?? 0}
                          onChange={val => field.onChange(val)}
                          disabled={isReadOnly}
                        />
                      )}
                    />
                  </div>
                )}
              </Section>

              {/* Status info in view mode */}
              {mode === 'view' && punchItem && (
                <Section title="Status & Tracking" icon={<Tag className="h-4 w-4" />} defaultOpen>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-xs text-gray-500">Status</span>
                      <p className="font-medium capitalize text-gray-800">
                        {punchItem.status.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Created By</span>
                      <p className="font-medium text-gray-800">{punchItem.createdByName}</p>
                    </div>
                    {punchItem.verifiedByName && (
                      <div>
                        <span className="text-xs text-gray-500">Verified By</span>
                        <p className="font-medium text-gray-800">{punchItem.verifiedByName}</p>
                      </div>
                    )}
                    {punchItem.rejectionReason && (
                      <div className="col-span-2">
                        <span className="text-xs text-gray-500">Rejection Reason</span>
                        <p className="font-medium text-red-700">{punchItem.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                </Section>
              )}
            </form>
          </div>

          {/* Footer */}
          {!isReadOnly && (
            <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex justify-end space-x-3 flex-shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="punch-item-form"
                disabled={isLoading}
                className="px-5 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:bg-brand-200 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{mode === 'create' ? 'Creating...' : 'Saving...'}</span>
                  </>
                ) : (
                  <span>{mode === 'create' ? 'Create Punch Item' : 'Save Changes'}</span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default PunchItemDrawer;
